import {
  type CookieValueTypes,
  type OptionsType,
  getCookie,
  setCookie,
} from 'cookies-next/client'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { v4 } from 'uuid'
import useExpiringLocalStorage from '~/src/hooks/useExpiringLocalStorage'
import { MAX_AGE_OF_COOKIE, TEMP_USER_ID_KEY } from '~/src/lib/constants'

const useGetTempUserId = () => {
  const posthog = usePostHog()

  const existingTempUserIdCookie = getCookie(TEMP_USER_ID_KEY)
  const [tempUserId, setTempUserId] = useState<CookieValueTypes>(
    existingTempUserIdCookie
  )

  // Memoize expiration date to prevent unnecessary re-renders
  const expires = useMemo(
    () => new Date(Date.now() + 1000 * MAX_AGE_OF_COOKIE),
    []
  )

  const [tempUserIdLocalStorage, setTempUserIdLocalStorage] =
    useExpiringLocalStorage<CookieValueTypes>(
      TEMP_USER_ID_KEY,
      existingTempUserIdCookie,
      {
        expires,
        maxAge: MAX_AGE_OF_COOKIE,
      }
    )

  useEffect(() => {
    if (tempUserId) return

    const fallbackId = v4()

    try {
      // Mint immediately — do NOT wait for the PostHog SDK. Gating on
      // `posthog.__loaded` meant a cold first visit (or any visitor whose
      // ad blocker stops PostHog from ever loading) had NO tempUserId when
      // the first chat turn fired, and the brain controller silently skips
      // persistence for identity-less requests — the thread streamed, the
      // URL minted its threadId, and the row never existed (404 on
      // reload). Prefer the PostHog distinct_id when the SDK happens to be
      // ready (keeps analytics ids aligned), else mint a v4 now; the
      // alias branch below reconciles the ids with PostHog on a later
      // mount once the SDK is up.
      const distinctId = posthog.__loaded ? posthog.get_distinct_id() : null
      const uuid = existingTempUserIdCookie || distinctId || fallbackId

      if (!existingTempUserIdCookie) {
        const cookieOptions: OptionsType = {
          expires,
          maxAge: MAX_AGE_OF_COOKIE,
          path: '/',
        }

        if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
          cookieOptions.domain = '.dupe.com'
        }

        setCookie(TEMP_USER_ID_KEY, uuid, cookieOptions)

        setTempUserIdLocalStorage(uuid)
      } else {
        // alias existing users (only once the SDK is actually up — pre-load
        // we don't know the real distinct_id to compare against; the next
        // mount with a loaded SDK takes this branch and reconciles)
        if (
          posthog.__loaded &&
          typeof existingTempUserIdCookie === 'string' &&
          existingTempUserIdCookie !== distinctId
        ) {
          posthog.alias(existingTempUserIdCookie)
        }
      }

      setTempUserId(uuid)
    } catch (error) {
      console.error('Error in useGetTempUserId:', error)
      // Fallback to using uuid if everything else fails
      setTempUserId(fallbackId)
    }
  }, [
    posthog,
    tempUserId,
    tempUserIdLocalStorage,
    expires,
    setTempUserIdLocalStorage,
    existingTempUserIdCookie,
  ])

  const tempUserIdValue = tempUserId?.toString()

  return { tempUserId: tempUserIdValue }
}

export default useGetTempUserId
